/**
 * Domain types — mirror the FastAPI response schemas. Keep these in sync
 * with `app/schemas/*.py`. We deliberately don't auto-generate (yet) so
 * frontend developers see plain TS interfaces rather than codegen output.
 */

export interface BackofficeUser {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_active: boolean;
  totp_enabled: boolean;
  locale: string | null;
}

export interface LoginRequest {
  email: string;
  password: string;
  totp_code?: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  expires_in: number;
  user: BackofficeUser;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  inn: string | null;
  status: "active" | "suspended" | "pending_verification";
  contact_email: string | null;
  phone: string | null;
  address: string | null;
  created_at: string;
  updated_at: string;
}

export interface CompanyCreate {
  // Lower-case, digits, hyphens — used as the URL-safe ID.
  slug: string;
  name: string;
  // Required by backend (validation), 9 digits.
  inn: string;
  contact_email?: string | null;
  phone?: string | null;
  address?: string | null;
}

export interface CompanyUpdate {
  name?: string;
  inn?: string | null;
  contact_email?: string | null;
  phone?: string | null;
  address?: string | null;
  status?: Company["status"];
}

export interface Application {
  id: string;
  company_id: string;
  slug: string;
  name: string;
  client_id: string;
  status: "active" | "suspended";
  monthly_quota: number | null;
  threshold_default: number;
  webhook_url: string | null;
  is_web: boolean;
  is_mobile: boolean;
  // ─── Security checks (super-admin only, all default true) ────────
  /** Passive anti-spoof + deepfake screen. Off → a printed photo passes. */
  check_spoof: boolean;
  /** Active head-turn challenges. Off → the SDK gets an EMPTY `challenges`. */
  check_liveness: boolean;
  /**
   * The 5-minute window every signed camera frame must fall inside, measured
   * against the DEVICE's clock. Off → frames are still signed and verified,
   * only the timestamp is not required to be recent, which is the one way a
   * user with a wrong phone/computer clock can pass at all.
   *
   * OPTIONAL because the backend may not serve it yet: an absent value means
   * ON, exactly like the `true` default it will arrive with.
   */
  check_frame_time?: boolean;
  /**
   * Apple App Attest on iOS captures: proves the request came from a genuine,
   * unmodified copy of the host app on a real Apple device. Off → an iOS
   * capture from a repackaged or emulated app is no longer rejected.
   *
   * OPTIONAL for the same reason as `check_frame_time`: absent reads as ON.
   */
  check_app_attest?: boolean;
  /**
   * The Apple App IDs (`TEAMID.bundle.id`) allowed to attest for this
   * application — our SDK ships inside several host apps, each with its own.
   * `null` (or absent) = no list, and the server falls back to its global
   * default App ID.
   */
  ios_app_ids?: string[] | null;
  /**
   * Which head-pose actions the two challenges are drawn from.
   * `null` = all four. Production measured pitch failing 3.2x more often
   * than yaw, so narrowing this is the lever for a struggling app.
   */
  allowed_challenges: ChallengeType[] | null;
  created_at: string;
}

/** The four actions the verifier understands, in panel order. */
export const CHALLENGE_TYPES = [
  "head_yaw_left",
  "head_yaw_right",
  "head_pitch_up",
  "head_pitch_down",
] as const;

export type ChallengeType = (typeof CHALLENGE_TYPES)[number];

// ─── Monitoring ────────────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
  /**
   * OPTIONAL. True when `total` is a ceiling the backend stopped counting at
   * rather than the real row count — an exact `COUNT(*)` over millions of
   * rows costs more than the page itself. The UI must then render it as
   * "10 000+", because a capped count presented as exact is a wrong number.
   * Absent on every endpoint that still counts exactly.
   */
  total_is_capped?: boolean;
}

/** Where the session was started from — an SDK integration or the mobile app. */
export type SessionOrigin = "sdk" | "app_login" | "qr_login";

export interface VerificationSession {
  id: string;
  client_id: string;
  method: string;
  status: "pending" | "in_progress" | "passed" | "failed" | "expired" | "cancelled";
  pinfl: string | null;
  birth_date: string;
  attempts: number;
  max_attempts: number;
  similarity: number | null;
  liveness_score: number | null;
  quality_score: number | null;
  failure_reason: string | null;
  reuid: string | null;
  ip_address: string | null;
  created_at: string;
  completed_at: string | null;
  expires_at: string;
  has_govt_photo: boolean;
  has_selfie: boolean;
  frame_count: number;
  // ─── Ownership ───────────────────────────────────────────────────
  company_id: string | null;
  company_name: string | null;
  // null for bare seeded clients that were never attached to an
  // application — the UI falls back to `client_name` and flags the row.
  application_id: string | null;
  application_name: string | null;
  client_name: string | null;
  origin: SessionOrigin;
  // ─── How it actually ran ─────────────────────────────────────────
  ml_backend: string | null; // "gpu" | "cpu" | "gpu+cpu"
  /** The similarity threshold THIS session was judged against. */
  threshold: number;
  duration_ms: number | null;
  /**
   * The application's security switches AS THEY APPLIED to this session — a
   * `false` means that check never ran, so the verdict is weaker than a
   * default one and the UI must say so. Backs both the list row and the
   * detail response.
   */
  check_spoof: boolean;
  check_liveness: boolean;
}

export interface ApiLog {
  id: number;
  client_id: string | null;
  endpoint: string;
  method: string;
  status_code: number;
  duration_ms: number;
  success: boolean;
  error_code: string | null;
  ip_address: string | null;
  cached: boolean;
  timestamp: string;
}

export interface AuditLog {
  id: number;
  session_id: string | null;
  client_id: string | null;
  event_type: string;
  actor_type: string;
  actor_id: string | null;
  ip_address: string | null;
  request_id: string | null;
  payload: Record<string, unknown> | null;
  created_at: string;
}

export interface AuditLogFilters {
  event_type?: string;
  actor_type?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface DailyCount {
  day: string;
  total: number;
  passed: number;
  failed: number;
  /** pending + in_progress + expired + cancelled — no verdict reached. */
  abandoned: number;
}

export interface DashboardStats {
  sessions_today: number;
  sessions_total: number;
  /** passed / (passed + failed) — same definition as SessionStats.pass_rate. */
  success_rate: number;
  /** Sessions that reached no verdict, over the window. */
  abandon_rate: number;
  window_passed: number;
  window_failed: number;
  window_abandoned: number;
  active_companies: number;
  avg_duration_ms: number | null;
  daily: DailyCount[];
}

export interface SessionFilters {
  status?: string;
  method?: string;
  company_id?: string;
  application_id?: string;
  client_id?: string;
  origin?: SessionOrigin;
  failure_reason?: string;
  ml_backend?: string;
  /** PINFL substring. */
  search?: string;
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface FailureReasonCount {
  reason: string;
  count: number;
}

export interface ThresholdCount {
  threshold: number;
  count: number;
}

/** Aggregates for the current session filter set (`/sessions/stats`). */
export interface SessionStats {
  total: number;
  passed: number;
  failed: number;
  pending: number;
  in_progress: number;
  expired: number;
  cancelled: number;
  /** passed / (passed + failed) — terminal sessions only. */
  pass_rate: number;
  avg_similarity: number | null;
  avg_liveness: number | null;
  avg_duration_ms: number | null;
  by_failure_reason: FailureReasonCount[];
  /**
   * Thresholds present across the whole filtered set, count desc then
   * threshold asc. `sessions.threshold` is NOT NULL, so every session lands
   * in exactly one bucket and the counts sum to `total`. More than one entry
   * means this filter spans sessions judged by different rules.
   */
  by_threshold: ThresholdCount[];
}

export interface SessionFilterCompany {
  id: string;
  name: string;
}

export interface SessionFilterApplication {
  id: string;
  name: string;
  company_id: string;
}

export interface SessionFilterOptions {
  companies: SessionFilterCompany[];
  applications: SessionFilterApplication[];
  /**
   * The DISTINCT `ml_backend` values actually present, plus a sentinel for
   * sessions that were never analysed (`ml_backend IS NULL`). Driven by the
   * API rather than hardcoded so combined values like "gpu+cpu" stay
   * reachable — the filter is an exact match.
   */
  ml_backends: string[];
}

// ─── Verified identities (super-admin only) ────────────────────────

export interface VerifiedIdentity {
  id: string;
  pinfl: string;
  // null while the IMV prefetch hasn't landed yet — a normal state, not an error.
  passport: string | null;
  birth_date: string | null;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  last_verified_at: string | null;
  verification_count: number;
  created_at: string;
  updated_at: string;
  has_passport_image: boolean;
  has_verification_image: boolean;
  // Reserved for the 1:N work — always false today.
  has_embedding: boolean;
}

export interface IdentityFilters {
  // Matches PINFL or any name part, case-insensitive.
  search?: string;
  limit?: number;
  offset?: number;
}

// ─── Mobile app (super-admin only) ─────────────────────────────────

export type MobileUserStatus = "active" | "suspended" | "deleted";

export interface MobileUser {
  id: string;
  pinfl: string;
  passport: string | null;
  birth_date: string | null;
  first_name: string | null;
  last_name: string | null;
  middle_name: string | null;
  phone_number: string | null;
  status: MobileUserStatus;
  preferred_language: string;
  reuid: string | null;
  enrolled_session_id: string | null;
  last_login_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  // Aggregates: always present on the detail response (see MobileUserDetail),
  // optional on list rows so the table degrades to "—" instead of breaking.
  device_count?: number;
  login_count?: number;
}

export interface MobileUserDetail extends MobileUser {
  device_count: number;
  login_count: number;
  last_event_at: string | null;
  has_photo: boolean;
}

export interface MobileDevice {
  id: string;
  app_user_id: string;
  device_uid: string;
  platform: "ios" | "android";
  model: string | null;
  os_version: string | null;
  status: "active" | "revoked";
  last_seen_at: string | null;
  revoked_at: string | null;
  created_at: string;
}

// The cross-user device list joins the owner in — the per-user tab doesn't.
export interface MobileDeviceAdmin extends MobileDevice {
  owner_pinfl: string | null;
  owner_full_name: string | null;
}

export interface AccessEvent {
  id: string;
  app_user_id: string;
  client_id: string | null;
  system_name: string | null;
  event_type: "app_login" | "qr_login";
  result: "approved" | "rejected" | "failed";
  verification_session_id: string | null;
  qr_login_request_id: string | null;
  similarity: number | null;
  liveness_score: number | null;
  device_id: string | null;
  ip_address: string | null;
  created_at: string;
}

export interface AccessEventAdmin extends AccessEvent {
  owner_pinfl: string | null;
  owner_full_name: string | null;
}

export type QrRequestStatus =
  | "pending"
  | "scanned"
  | "approved"
  | "rejected"
  | "expired"
  | "consumed";

// request_code / callback_code are deliberately never returned by the API —
// they're login secrets, so they must not exist on this type either.
export interface QrRequestAdmin {
  id: string;
  client_id: string;
  system_name: string | null;
  status: QrRequestStatus;
  requested_scopes: string[];
  app_user_id: string | null;
  owner_pinfl: string | null;
  owner_full_name: string | null;
  verification_session_id: string | null;
  expires_at: string;
  scanned_at: string | null;
  completed_at: string | null;
  created_at: string;
}

export interface MobileStats {
  total_users: number;
  active_users: number;
  suspended_users: number;
  active_devices: number;
  logins_today: number;
  qr_approvals_today: number;
}

export interface MobileUserFilters {
  // Matches PINFL, any name part or phone, case-insensitive.
  search?: string;
  status?: MobileUserStatus;
  limit?: number;
  offset?: number;
}

export interface MobileDeviceFilters {
  // Matches device uid, model, owner PINFL or owner name.
  search?: string;
  platform?: MobileDevice["platform"];
  status?: MobileDevice["status"];
  limit?: number;
  offset?: number;
}

export interface AccessEventFilters {
  event_type?: AccessEvent["event_type"];
  result?: AccessEvent["result"];
  date_from?: string;
  date_to?: string;
  limit?: number;
  offset?: number;
}

export interface QrRequestFilters {
  status?: QrRequestStatus;
  limit?: number;
  offset?: number;
}

// ─── Service catalog (partner directory for the mobile app) ────────

/** How the card opens: a web page, or an installed/installable app. */
export type ServiceType = "web" | "mobile";

export interface ServiceCategory {
  id: string;
  /** Machine key the app groups by — set once, never edited. */
  key: string;
  // Every name is nullable, so the UI falls back through the other
  // locales and finally to `key`, which always exists.
  name_uz: string | null;
  name_ru: string | null;
  name_en: string | null;
  /** Lower is shown first. */
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceCategoryCreate {
  key: string;
  name_uz?: string | null;
  name_ru?: string | null;
  name_en?: string | null;
  order?: number;
  is_active?: boolean;
}

/**
 * PATCH — `key` is deliberately absent: it is immutable.
 *
 * The backend schema is `extra="forbid"`, so this is the WHOLE accepted body:
 * spreading a `ServiceCategory` row in here (`{...category, name_uz}`) is a
 * 422 on `id` / `key` / `created_at` / `updated_at`. Build the payload field
 * by field.
 */
export interface ServiceCategoryUpdate {
  name_uz?: string | null;
  name_ru?: string | null;
  name_en?: string | null;
  order?: number;
  is_active?: boolean;
}

export interface Service {
  id: string;
  category_id: string;
  // Category identity denormalised onto the row, so the table needs no join.
  category_key: string;
  category_name_uz: string | null;
  category_name_ru: string | null;
  category_name_en: string | null;
  // Optional link to a tenant — a partner is not required to be one.
  company_id: string | null;
  company_name: string | null;
  /** Brand name — NOT localized. */
  name: string;
  type: ServiceType;
  logo_url: string | null;
  // The backend rejects a card the app cannot open: `web` needs `url`,
  // `mobile` needs at least one of ios_url / android_url / deeplink.
  url: string | null;
  ios_url: string | null;
  android_url: string | null;
  deeplink: string | null;
  /** Lower is shown first, within the category. */
  order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceCreate {
  category_id: string;
  company_id?: string | null;
  name: string;
  type: ServiceType;
  logo_url?: string | null;
  url?: string | null;
  ios_url?: string | null;
  android_url?: string | null;
  deeplink?: string | null;
  order?: number;
  is_active?: boolean;
}

/**
 * PATCH — partial; nulls clear a link the other `type` no longer uses.
 *
 * Derived from `ServiceCreate`, not from `Service`, so the read-only join
 * fields (`category_key`, `category_name_*`, `company_name`) can't be sent
 * back. This schema doesn't forbid extras today; the category one does.
 */
export type ServiceUpdate = Partial<ServiceCreate>;

/**
 * Reply of `POST /v2/admin/services/logo` — the value to store in
 * `Service.logo_url`, which still accepts a partner's CDN URL just as before.
 *
 * Absolute (`https://host/media/service/<uuid>.<ext>`) when the backend has
 * `PUBLIC_BASE_URL` configured, and the bare path when it doesn't — the DB
 * stores the path either way. Both render as an `<img src>` from the admin
 * origin, so nothing downstream has to care which one arrived. nginx serves
 * that directory directly; there is no API endpoint behind it.
 */
export interface ServiceLogoUpload {
  logo_url: string;
}

export interface ServiceFilters {
  category_id?: string;
  type?: ServiceType;
  is_active?: boolean;
  /** Brand-name substring. */
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ApiLogFilters {
  endpoint?: string;
  status_code?: number;
  success?: boolean;
  limit?: number;
  offset?: number;
}

export interface ApplicationCreate {
  company_id: string;
  slug: string;
  name: string;
  description?: string | null;
  is_web?: boolean;
  is_mobile?: boolean;
  threshold_default?: number;
  monthly_quota?: number | null;
  webhook_url?: string | null;
}

// POST /applications returns the plaintext secrets exactly once.
export interface ApplicationCreated extends Application {
  client_secret: string;
  webhook_secret: string;
}

// POST /applications/{id}/rotate-webhook-secret — new secret, shown once.
export interface WebhookSecretRotated {
  webhook_secret: string;
}

// POST /applications/{id}/rotate-client-secret — new secret, shown once.
export interface ClientSecretRotated {
  client_secret: string;
}

/**
 * PATCH /applications/{id} — fully partial; send ONLY what changed.
 *
 * `check_spoof` / `check_liveness` / `check_frame_time` / `check_app_attest`
 * and `ios_app_ids` are super-admin only: including any one of them as a
 * lesser role is a 403 (`SECURITY_CHECKS_SUPER_ADMIN_ONLY`), even when the
 * value is unchanged. That is why the caller diffs before building the payload.
 */
export interface ApplicationUpdate {
  threshold_default?: number;
  check_spoof?: boolean;
  check_liveness?: boolean;
  check_frame_time?: boolean;
  check_app_attest?: boolean;
  /** At least two, or `null` to restore all four. Super-admin only. */
  allowed_challenges?: ChallengeType[] | null;
  /**
   * The allowed Apple App IDs, replaced wholesale. Omitted = leave unchanged;
   * `[]` = clear the list (the server stores and returns `null`, falling back
   * to its global default App ID). Super-admin only.
   */
  ios_app_ids?: string[];
}

// ─── Credential share (one-time link) ──────────────────────────────

// POST /applications/{id}/credential-share — mints a one-time link.
// Side effect: rotates client_secret + webhook_secret.
export interface CredentialShareCreated {
  token: string;
  expires_at: string;
}

// GET /share/{token} — PUBLIC, does not consume.
export interface PublicShareStatus {
  status: "active" | "consumed" | "expired" | "invalid";
  application_name: string | null;
}

// POST /share/{token}/consume — PUBLIC, one-time reveal of the secrets.
export interface CredentialSharePayload {
  client_id: string;
  client_secret: string;
  webhook_secret: string;
  application_name: string;
}

// ─── Members ───────────────────────────────────────────────────────

export type MemberRole = "owner" | "admin" | "viewer";

export interface Member {
  id: string;
  company_id: string;
  user_id: string;
  role: MemberRole;
  email: string | null;
  full_name: string | null;
  is_deleted: boolean;
  created_at: string;
}

export interface MemberInvite {
  company_id: string;
  user_email: string;
  role: MemberRole;
  full_name?: string | null;
  pinfl?: string | null;
  phone?: string | null;
}

// POST /members — temp_password set only when a new user was created.
export interface MemberInvited extends Member {
  temp_password: string | null;
}

// ─── Backoffice users (admin panel Users page) ─────────────────────

export type AdminRole =
  | "super_admin"
  | "moderator"
  | "bookkeeping"
  | "support"
  | "readonly"
  | "company_viewer";

/**
 * Everything except `company_viewer` is OURS. A company viewer is a
 * customer: the API scopes every read to the companies they are a member
 * of, and the panel hides the surfaces that are about us rather than them.
 *
 * `readonly` is NOT in that group — it is staff who cannot write, and it
 * still sees every tenant. Mixing the two up is the whole reason the
 * backend carries an explicit role instead of inferring one.
 */
export const STAFF_ROLES: AdminRole[] = [
  "super_admin",
  "moderator",
  "bookkeeping",
  "support",
  "readonly",
];

/**
 * Takes a plain string, not an `AdminRole`: the role the panel actually
 * holds arrives in a JWT the server signed, and a value this build has
 * never heard of is exactly the case that must not be treated as staff.
 * Unknown → false, which fails closed.
 */
export function isStaffRole(role: string | undefined | null): boolean {
  return !!role && (STAFF_ROLES as string[]).includes(role);
}

export interface AdminUser {
  id: string;
  email: string;
  full_name: string | null;
  role: AdminRole;
  is_active: boolean;
  is_2fa_enabled: boolean;
  last_login_at: string | null;
  created_at: string;
}

export interface AdminUserCreate {
  email: string;
  password: string;
  full_name?: string | null;
  role: AdminRole;
}

export interface AdminUserUpdate {
  full_name?: string | null;
  role?: AdminRole;
  is_active?: boolean;
}

// ─── Analytics boards ───────────────────────────────────────────────
// Deliberately the same filter vocabulary as SessionFilters minus paging —
// the boards and the sessions list must never describe different populations.
export interface AnalyticsFilters {
  status?: string;
  method?: string;
  origin?: SessionOrigin;
  company_id?: string;
  application_id?: string;
  client_id?: string;
  failure_reason?: string;
  ml_backend?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
}

export interface FunnelStage {
  key: string;
  count: number;
  /** Share of the PREVIOUS stage — localises a leak. */
  of_previous: number;
  /** Share of the first stage — sizes it. */
  of_total: number;
  lost: number;
}

export interface Abandonment {
  never_started: number;
  gave_up_after_failure: number;
  attempts_exhausted: number;
}

export interface GateOutcome {
  key: string;
  reached: number;
  passed: number;
  rejected: number;
  /**
   * Reached this gate and it then did not run: switched off for the session,
   * or nothing to judge (the match gate is NULL for IDENTITY_NOT_FOUND and
   * GOVT_PHOTO_NO_FACE). Never folded into passed.
   */
  skipped: number;
  rejection_rate: number;
}

export interface AttemptOutcome {
  attempt_no: number;
  total: number;
  passed: number;
  pass_rate: number;
}

export interface FunnelResponse {
  sessions: { stages: FunnelStage[]; abandonment: Abandonment };
  gates: GateOutcome[];
  /**
   * 0 means "nothing recorded yet", NOT "nothing failed". The attempts table
   * was never written to before migration 0016 — check this before drawing
   * the gate chart.
   */
  attempts_total: number;
  by_attempt_no: AttemptOutcome[];
}

export interface ScoreBin {
  /** Inclusive lower edge; width is bin_width. */
  lo: number;
  passed: number;
  /** Scored here and did not pass — rejected outright OR abandoned. */
  other: number;
}

export interface ThresholdShare {
  threshold: number;
  sessions: number;
}

export interface ScoresResponse {
  similarity: {
    bin_width: number;
    range: number[];
    bins: ScoreBin[];
    thresholds_in_force: ThresholdShare[];
    near_threshold: number;
    near_band: number;
  };
  /**
   * sessions.liveness_score = Aggregated.composite — a weighted blend that
   * NO threshold is ever applied to, so it carries no gate. Drawing the
   * 0.85 median gate here would place passing traffic left of the line.
   */
  liveness: {
    bin_width: number;
    range: number[];
    bins: ScoreBin[];
  };
  /** attempts.scores.liveness_median — the quantity the gate really judges. */
  liveness_median: {
    bin_width: number;
    range: number[];
    bins: ScoreBin[];
    median_gate: number;
    /** 0 both when nothing scored and when nothing is recorded yet. */
    sample_size: number;
  };
}

export interface DailyFailures {
  day: string;
  reasons: Record<string, number>;
}

export interface SpoofDetail {
  /** 0 both when nothing was rejected and when nothing was recorded yet. */
  sample_size: number;
  median: number | null;
  median_min: number | null;
  median_max: number | null;
  variance: number | null;
  samples: number | null;
  median_gate: number;
}

export interface FailuresResponse {
  daily: DailyFailures[];
  spoof: SpoofDetail;
}

// ─── Session forensics ──────────────────────────────────────────────
export interface FrameDiagnostic {
  index: number;
  face: boolean;
  multiple_faces: boolean;
  /** Present only when a face was detected — never a fabricated 0. */
  detection?: number;
  quality?: number;
  liveness?: number;
  yaw?: number;
  pitch?: number;
  roll?: number;
  face_w?: number;
  face_h?: number;
}

export interface Criterion {
  key: string;
  value: number | null;
  threshold: number | null;
  /** The direction that PASSES. Only the deepfake score passes by staying below. */
  comparison: "gte" | "lte" | "lt";
  passed: boolean | null;
}

export interface GateEvaluation {
  key: string;
  /** "not_run" is NOT a kind of pass — the gate told you nothing. */
  state: "passed" | "rejected" | "not_run";
  /**
   * Every line the gate judged on. Liveness has four; publishing only the
   * median made a rejection on the trimmed minimum render as a satisfied
   * inequality next to the word REJECTED.
   */
  criteria: Criterion[];
  /**
   * "session" — the number snapshotted at creation, i.e. the one that really
   * judged this attempt. "config" — today's setting, which may have changed
   * since.
   */
  threshold_source: "session" | "config" | null;
  detail: Record<string, unknown> | null;
}

export interface AttemptDetail {
  attempt_no: number;
  created_at: string;
  duration_ms: number | null;
  frames_count: number;
  failure_reason: string | null;
  scores: Record<string, number>;
  stage_durations: Record<string, number>;
  frames: FrameDiagnostic[];
  gates: GateEvaluation[];
}

export interface RelatedSession {
  id: string;
  status: string;
  failure_reason: string | null;
  similarity: number | null;
  created_at: string;
}

export interface SessionForensics {
  session_id: string;
  attempts: AttemptDetail[];
  /**
   * The one attempt whose frame images are still on disk — frame_paths is
   * overwritten on every submission. null when none were kept.
   */
  frames_available_for_attempt_no: number | null;
  stored_frame_count: number;
  related: Record<string, RelatedSession[]>;
}

// ─── Performance board ──────────────────────────────────────────────
export interface EndpointLatency {
  endpoint: string;
  calls: number;
  p50_ms: number | null;
  p95_ms: number | null;
  p99_ms: number | null;
  errors: number;
  error_rate: number;
}

export interface BackendShare {
  /** "gpu" | "cpu" | "gpu+cpu" | "none" (never reached analysis). */
  backend: string;
  sessions: number;
  avg_duration_ms: number | null;
}

export interface StageTiming {
  stage: string;
  /** 0 both when the step never runs and when nothing is recorded yet. */
  samples: number;
  avg_ms: number | null;
  p95_ms: number | null;
}

export interface Throughput {
  peak_sessions_per_minute: number;
  peak_at: string | null;
  peak_rps: number;
  /** A load-test measurement, not a specification. */
  capacity_rps: number;
  capacity_measured_on: string;
  /** null when no capacity is configured. Peak honours filters; capacity is fleet-wide. */
  headroom: number | null;
  sessions_total: number;
}

export interface SlowSession {
  id: string;
  status: string;
  ml_backend: string | null;
  failure_reason: string | null;
  created_at: string;
  duration_ms: number | null;
}

export interface PerformanceResponse {
  /**
   * OPTIONAL. When set, the ENDPOINT table's p50/p95/p99 are estimated from
   * hourly histograms rather than computed over the rows, so each is only as
   * accurate as the latency bucket it falls in, and a value at or above this
   * number means "at least this much" — the histogram does not resolve
   * further. Absent or null: the percentiles are exact. Nothing else on this
   * board is affected; the stage timings are their own measurement.
   */
  endpoints_percentiles_capped_at_ms?: number | null;
  endpoints: EndpointLatency[];
  backends: BackendShare[];
  stages: StageTiming[];
  throughput: Throughput;
  slowest: SlowSession[];
}

// ─── Tenants board ──────────────────────────────────────────────────
export interface TenantRow {
  application_id: string;
  application_name: string;
  slug: string;
  status: string;
  company_name: string | null;
  sessions: number;
  terminal: number;
  passed: number;
  pass_rate: number;
  abandon_rate: number;
  avg_similarity: number | null;
  avg_liveness: number | null;
  p95_duration_ms: number | null;
  /** The rules this tenant is actually judged by. */
  threshold_default: number;
  check_spoof: boolean;
  check_liveness: boolean;
  monthly_quota: number | null;
  /** null = the Redis counter could not be read. Never conflate with 0. */
  /** Always calendar-month-to-date — ignores the board's date range. */
  quota_used: number | null;
  quota_projected: number | null;
  is_outlier: boolean;
}

export interface FleetStats {
  mean_pass_rate: number;
  stdev: number;
  floor: number;
  sample: number;
  min_terminal: number;
}

export interface TenantsResponse {
  applications: TenantRow[];
  /** null when fewer than THREE applications clear min_terminal — at
   *  two, mean - 1*stdev equals the lower rate and nothing can fire. */
  fleet: FleetStats | null;
}

// ─── SDK telemetry boards ───────────────────────────────────────────
// Mirrors app/schemas/analytics.py (the `SDK telemetry` block).
//
// Deliberately NOT AnalyticsFilters: that vocabulary filters `sessions`, and
// every predicate in it presumes a session exists. Applied to telemetry it
// would silently delete the flows that never minted one — the camera
// permission denials and the failed mints, i.e. the population this table
// was built to see.
export interface SdkFilters {
  /** Bounds `received_at`, the SERVER clock — a phone with a wrong clock is common. */
  date_from?: string;
  date_to?: string;
  /**
   * The tenant's Client uuid. This is the one that AUTHORISES a number:
   * `app_bundle_id` is caller-supplied and a competitor's bundle id is
   * public, so a bundle filter segments without proving whose traffic it is.
   * Set from a link rather than typed — there is no client picker.
   */
  client_id?: string;
  /** "android" | "ios". Never leave it open on a device panel. */
  platform?: string;
  app_bundle_id?: string;
  sdk_version?: string;
  device_profile_id?: string;
  /** Emulator + debuggable builds. Off by default; counted either way in `coverage`. */
  include_test_traffic?: boolean;
}

/**
 * How much data the numbers beside it stand on. READ THIS FIRST.
 *
 * `sdk_flows` is empty until the Android build carrying schema_version 3
 * ships, and an empty table renders "0% abandonment" exactly as convincingly
 * as a healthy one does. No default can substitute for this block.
 */
export interface TelemetryCoverage {
  window_from: string;
  window_to: string;
  received: number;
  /** The denominator of EVERY rate on the board: received minus test traffic. */
  counted: number;
  excluded_emulator: number;
  excluded_debuggable: number;
  /** NOT the sum of the two above — one row can be both. */
  excluded_total: number;
  test_traffic_included: boolean;
  /** false = the table has NEVER received a row. Say "waiting", not "0%". */
  any_flows_received: boolean;
}

export interface SdkAbandonmentStage {
  stage: string;
  flows: number;
  of_abandoned: number;
  /** The percentile denominator, and never `flows` — a flow can record where
   *  it stopped without reporting the dwell. */
  samples: number;
  /** null = nobody reported a dwell. Never 0, which would claim they left instantly. */
  p50_ms: number | null;
  p90_ms: number | null;
}

export interface SdkErrorKind {
  error_kind: string;
  flows: number;
  /** Of every counted flow, not of the errors alone. */
  of_flows: number;
}

export interface SdkAttemptRow {
  session_attempt: number;
  flows: number;
  /** DISTINCT session ids — the number that makes retry uptake readable. */
  sessions: number;
  reached_result: number;
  abandoned: number;
  abandon_rate: number;
  passed: number;
}

export interface SdkSessionJoin {
  flows: number;
  with_session_id: number;
  /** Ended before a session existed — camera denied, mint failed. */
  sessionless: number;
  matched: number;
  /** Had a session id that resolves to nothing: pruned, or unattributable. */
  unmatched: number;
  matched_passed: number;
  matched_failed: number;
  matched_open: number;
}

export interface SdkFunnelResponse {
  coverage: TelemetryCoverage;
  /** Same shape as the session funnel, so the two read alike when stacked. */
  stages: FunnelStage[];
  /** Flows with no recognised stage (REGISTRATION, QR_LOGIN). Kept OUT of
   *  LAUNCHED so the top of the funnel is not inflated. */
  unstaged: number;
  abandoned: number;
  /**
   * OPTIONAL. When set, the ABANDONMENT panel's dwell percentiles (`p50_ms`
   * / `p90_ms` below) are estimated from hourly histograms, so each is only
   * as accurate as the latency bucket it falls in, and a value at or above
   * this number means "at least this much". Absent or null: they are exact.
   *
   * It does NOT make a null dwell into a measured one — the two say
   * different things and both have to survive: null is "nobody reported how
   * long they sat there", this is "we know, to within a bucket".
   */
  abandonment_percentiles_capped_at_ms?: number | null;
  abandonment: SdkAbandonmentStage[];
  /** The 25 largest. Compare with `error_kind_count` before reading it as all. */
  errors: SdkErrorKind[];
  /**
   * DISTINCT error kinds the window held. `error_kind` is meant to be low
   * cardinality, so a count near or above the cap is itself the finding — an
   * SDK build may be inventing class names.
   */
  error_kind_count: number;
  by_attempt: SdkAttemptRow[];
  session_join: SdkSessionJoin;
}

export interface SdkDeviceGroup {
  /** null when the device block never arrived — still a flow that abandoned. */
  key: string | null;
  platform: string;
  flows: number;
  abandoned: number;
  abandon_rate: number;
  frame_samples: number;
  /** null when nothing in the group reported frame counts (Android-only). */
  dropped_frame_ratio: number | null;
  device_models: string[];
}

export interface SdkDeviceGrouping {
  groups: SdkDeviceGroup[];
  /** Exceeds groups.length when the list is a top-N. */
  group_count: number;
  suppressed_groups: number;
  /** What the threshold kept out — a hundred models failing five times each. */
  suppressed_flows: number;
}

export interface SdkApiLevelRow {
  /** null on iOS (no such field) and when the device block never arrived.
   *  There is no API level 0. */
  api_level: number | null;
  platform: string;
  flows: number;
  abandoned: number;
  abandon_rate: number;
}

export interface SdkDensityRow {
  screen_density_dpi: number | null;
  platform: string;
  flows: number;
  /** The real denominator — avg and p90 skip NULLs. */
  flap_samples: number;
  avg_flap_count: number | null;
  p90_flap_count: number | null;
}

export interface SdkTimings {
  processing_samples: number;
  processing_p50_ms: number | null;
  processing_p90_ms: number | null;
  processing_p99_ms: number | null;
  /** upload_ms is optional even at schema_version 3 — its own denominator. */
  upload_samples: number;
  upload_p50_ms: number | null;
  upload_p90_ms: number | null;
  upload_p99_ms: number | null;
  cancelled_samples: number;
  cancelled_during_processing: number;
  /** null = nothing reported the flag, which is not "nobody cancelled". */
  cancelled_rate: number | null;
}

/**
 * One host app that embeds the SDK.
 *
 * A bundle id is caller-supplied and public, so without a `client_id` filter
 * these rows are indicative rather than authoritative — anyone holding a
 * valid SDK token can file flows under someone else's bundle. Filtered by
 * tenant, what remains under a bundle is what that tenant actually sent.
 */
export interface SdkIntegratorRow {
  app_bundle_id: string;
  flows: number;
  abandoned: number;
  abandon_rate: number;
  sdk_versions: string[];
}

export interface SdkVersionRow {
  app_bundle_id: string;
  /** Nullable although the column is NOT NULL today; nulls sort last. */
  sdk_version: string | null;
  flows: number;
  abandoned: number;
  abandon_rate: number;
}

/**
 * The release ranking. Same field names as `SdkDeviceGrouping` on purpose —
 * one suppression footer renders both.
 *
 * `suppressed_groups` matters more here than anywhere else on the board: a
 * build that shipped this morning is ALWAYS below the threshold, so a
 * version missing from `groups` usually means "too new to judge", not "no
 * problems".
 */
export interface SdkVersionGrouping {
  /** Grouped by bundle, biggest integrator first, in release order within a
   *  bundle — so the before/after pair sits together. Do not re-sort. */
  groups: SdkVersionRow[];
  group_count: number;
  suppressed_groups: number;
  suppressed_flows: number;
}

export interface SdkDevicesResponse {
  coverage: TelemetryCoverage;
  /** Flows a group needs before its rate is published. Shown, never assumed. */
  min_flows: number;
  by_device: SdkDeviceGrouping;
  by_hardware: SdkDeviceGrouping;
  by_api_level: SdkApiLevelRow[];
  by_screen_density: SdkDensityRow[];
  timings: SdkTimings;
  integrators: SdkIntegratorRow[];
  /** Bundles in the window against the 25 `integrators` returns. A cut list
   *  must never read as a complete one. */
  integrator_count: number;
  /** One row per (host app × SDK build) — the release-regression table. */
  integrator_versions: SdkVersionGrouping;
}

// ─── SDK filter options ─────────────────────────────────────────────
// `sdk_version` and `app_bundle_id` were free-text inputs matched exactly,
// which means an operator had to already know the string to filter by it —
// so in practice there was no version filter at all. These are the values
// that actually occur, so the control can offer them.

/**
 * What narrows the KNOWN options: window and tenant only.
 *
 * Deliberately not `SdkFilters`. Sending `platform` or `sdk_version` would
 * make the list depend on the choice it exists to offer — picking a version
 * would shrink the list of versions to that one. Platform narrowing is done
 * client side against each row's own `platform`.
 */
export interface SdkOptionsQuery {
  date_from?: string;
  date_to?: string;
  client_id?: string;
}

export interface SdkVersionOption {
  /** Carried per version because the same string can exist on both. */
  platform: string;
  sdk_version: string;
  /** Shown beside the version: a build with 4 flows is not a release. */
  flows: number;
}

export interface SdkBundleOption {
  app_bundle_id: string;
  flows: number;
}

export interface SdkFilterOptions {
  platforms: string[];
  sdk_versions: SdkVersionOption[];
  app_bundle_ids: SdkBundleOption[];
}

// ─── SDK versions comparison ────────────────────────────────────────

/**
 * One SDK build's whole window, as its own row.
 *
 * Unlike `SdkVersionRow` (the release table on the devices board, which
 * carries a delta against the row above it and must therefore keep the
 * server's order), these rows stand alone — so this table is safe to sort.
 */
export interface SdkVersionComparisonRow {
  platform: string;
  sdk_version: string;
  flows: number;
  /**
   * 0..1 of its OWN PLATFORM's flows in the window — not of the whole
   * window. So the shares inside one platform group sum to 1, and an iOS
   * build's 40% does not mean 40% of all traffic. Pooling the two would be
   * the same mistake the rest of this board exists to avoid: Android and iOS
   * are two populations and are never averaged.
   */
  share: number;
  passed: number;
  failed: number;
  /** Reached a result that was neither pass nor fail. */
  inconclusive: number;
  cancelled: number;
  errors: number;
  /** 0..1. Its denominator is passed + failed, not `flows`. */
  pass_rate: number;
  /** null when the build recorded no errors at all — not an empty string. */
  top_error_kind: string | null;
  top_error_flows: number;
  /** YYYY-MM-DD. A build first seen yesterday cannot be judged yet. */
  first_seen: string;
  last_seen: string;
}

export interface SdkVersionsResponse {
  rows: SdkVersionComparisonRow[];
}

// ─── Rejected uploads (4xx / 499 on a session's upload calls) ───────

/**
 * The platform the rejected call came from. `other` is what the backend
 * reports when the User-Agent said nothing it recognises — a real bucket,
 * not an error, so it is labelled rather than hidden.
 */
export type UploadRejectionPlatform = "android" | "ios" | "web" | "other";

/**
 * One rejected upload attempt against a session.
 *
 * `error_code` / `error_detail` are nullable: a 499 (the client walked away
 * mid-upload) and a bare 4xx from outside the categorised paths carry
 * neither. `error_detail` on a 422 is a FIELD PATH (`body.frames`), not one
 * of the known sub-reasons — see `utils/rejections`, which shows anything
 * unknown raw instead of guessing at words for it.
 */
export interface UploadRejection {
  at: string;
  endpoint: string;
  method: string;
  status_code: number;
  error_code: string | null;
  error_detail: string | null;
  platform: UploadRejectionPlatform;
  duration_ms: number;
}

/**
 * Deliberately its own filter type rather than `AnalyticsFilters`: the stats
 * endpoint takes exactly these four, and the session vocabulary (status,
 * failure_reason, ml_backend) describes a verdict that a rejected upload
 * never reached. The range is capped at 31 days server side — the view
 * clamps before sending so the cap surfaces as a hint, not a 422.
 */
export interface UploadRejectionFilters {
  date_from?: string;
  date_to?: string;
  application_id?: string;
  platform?: string;
}

export interface UploadRejectionDay {
  /** YYYY-MM-DD. */
  date: string;
  /** ALL upload calls that day — the denominator, not just the bad ones. */
  uploads: number;
  rejected: number;
}

export interface UploadRejectionCategory {
  error_code: string | null;
  error_detail: string | null;
  count: number;
}

export interface UploadRejectionPlatformRow {
  platform: string;
  uploads: number;
  rejected: number;
}

export interface UploadRejectionStats {
  days: UploadRejectionDay[];
  by_category: UploadRejectionCategory[];
  by_platform: UploadRejectionPlatformRow[];
}
